// WalkthroughSteps.js - Reusable walkthrough step wrappers for various app sections
import React from 'react';
import { View } from 'react-native';
import { CopilotStep, walkthroughable } from 'react-native-copilot';

const WalkthroughableView = walkthroughable(View);

// Step order constants - organized by screen flow
export const STEP_ORDER = {
  // Header navigation (1-6) - defined in HeaderNavigation.js
  HOME_BUTTON: 1,
  LEARNING_BUTTON: 2,
  DASHBOARD_BUTTON: 3,
  TESTS_BUTTON: 4,
  COLLECTIONS_BUTTON: 5,
  STUDENTS_BUTTON: 6,

  // Student Home Screen (10-19)
  STUDENT_RESUME_CARD: 10,
  // AIT-250: Quick Actions row replaced by Recent Quizzes widget.
  // Order 11 retained so existing copilot tour ordinals stay stable.
  STUDENT_RECENT_QUIZZES: 11,
  STUDENT_UPCOMING_TESTS: 12,
  STUDENT_STREAK: 13,
  STUDENT_RECENT_ACTIVITIES: 14,

  // Teacher Home Screen (20-29)
  TEACHER_STATS: 20,
  TEACHER_QUICK_ACTIONS: 21,
  TEACHER_ACTIVITY_FEED: 22,
  TEACHER_LEADERBOARD: 23,
  TEACHER_ENGAGEMENT_CHART: 24,

  // Learning Screen - Sidebar (30-39)
  SIDEBAR_TABS: 30,
  CHAT_HISTORY: 31,
  SIDEBAR_CHAPTERS_TAB: 32,
  SIDEBAR_ANALYTICS_TAB: 33,
  SIDEBAR_TESTS_TAB: 34,

  // Learning Screen - Content (40-49)
  TOPIC_TILES: 40,
  AI_CHAT_DOCK: 41,
  VIEW_MODE_TOGGLE: 42,
  CONTENT_AREA: 43,
};

// Generic walkthrough step wrapper
export const WalkthroughStep = ({
  name,
  text,
  order,
  children,
  active = true,
  style
}) => {
  if (!active) {
    return <View style={style}>{children}</View>;
  }

  return (
    <CopilotStep text={text} order={order} name={name}>
      <WalkthroughableView style={style}>
        {children}
      </WalkthroughableView>
    </CopilotStep>
  );
};

// ============================================
// Student Home Screen Steps
// ============================================

export const ResumeCardStep = ({ children, style }) => (
  <WalkthroughStep
    name="student-resume-card"
    text="Continue where you left off! This card shows your last topic and lets you jump right back into learning."
    order={STEP_ORDER.STUDENT_RESUME_CARD}
    style={style}
  >
    {children}
  </WalkthroughStep>
);

// Home redesign (2026-09-16): this step now highlights the "This week" card.
// Tour order (11) and name are preserved to keep ordinals stable.
export const StudentRecentQuizzesStep = ({ children, style }) => (
  <WalkthroughStep
    name="student-recent-quizzes"
    text="Your week at a glance: learning days, minutes and quiz results."
    order={STEP_ORDER.STUDENT_RECENT_QUIZZES}
    style={style}
  >
    {children}
  </WalkthroughStep>
);

export const UpcomingTestsStep = ({ children, style }) => (
  <WalkthroughStep
    name="student-upcoming-tests"
    text="Keep track of your upcoming and available tests. Click any test to start it."
    order={STEP_ORDER.STUDENT_UPCOMING_TESTS}
    style={style}
  >
    {children}
  </WalkthroughStep>
);

export const StreakCardStep = ({ children, style }) => (
  <WalkthroughStep
    name="student-streak"
    text="What to do next, and why."
    order={STEP_ORDER.STUDENT_STREAK}
    style={style}
  >
    {children}
  </WalkthroughStep>
);

export const RecentActivitiesStep = ({ children, style }) => (
  <WalkthroughStep
    name="student-recent-activities"
    text="Your recent activity history. See what you've been working on and quickly jump back to past conversations."
    order={STEP_ORDER.STUDENT_RECENT_ACTIVITIES}
    style={style}
  >
    {children}
  </WalkthroughStep>
);

// ============================================
// Teacher Home Screen Steps
// ============================================

export const TeacherStatsStep = ({ children, style }) => (
  <WalkthroughStep
    name="teacher-stats"
    text="Overview of your class statistics: total students, learning activities, and engagement metrics."
    order={STEP_ORDER.TEACHER_STATS}
    style={style}
  >
    {children}
  </WalkthroughStep>
);

export const TeacherQuickActionsStep = ({ children, style }) => (
  <WalkthroughStep
    name="teacher-quick-actions"
    text="Quick access to teacher tools: create a test, add students, or view insights."
    order={STEP_ORDER.TEACHER_QUICK_ACTIONS}
    style={style}
  >
    {children}
  </WalkthroughStep>
);

export const ActivityFeedStep = ({ children, style }) => (
  <WalkthroughStep
    name="teacher-activity-feed"
    text="Real-time feed of student activities. See who's learning, what they're studying, and when."
    order={STEP_ORDER.TEACHER_ACTIVITY_FEED}
    style={style}
  >
    {children}
  </WalkthroughStep>
);

export const LeaderboardStep = ({ children, style }) => (
  <WalkthroughStep
    name="teacher-leaderboard"
    text="Top performing students ranked by engagement. Use this to identify your most active learners."
    order={STEP_ORDER.TEACHER_LEADERBOARD}
    style={style}
  >
    {children}
  </WalkthroughStep>
);

export const EngagementChartStep = ({ children, style }) => (
  <WalkthroughStep
    name="teacher-engagement-chart"
    text="Visual breakdown of student engagement by activity type: 3D models, videos, and quizzes."
    order={STEP_ORDER.TEACHER_ENGAGEMENT_CHART}
    style={style}
  >
    {children}
  </WalkthroughStep>
);

// ============================================
// Learning Screen Steps
// ============================================

export const SidebarTabsStep = ({ children, style }) => (
  <WalkthroughStep
    name="sidebar-tabs"
    text="Navigate between different sections: Chat history, Chapters & Topics, Analytics, and Tests."
    order={STEP_ORDER.SIDEBAR_TABS}
    style={style}
  >
    {children}
  </WalkthroughStep>
);

export const ChatHistoryStep = ({ children, style }) => (
  <WalkthroughStep
    name="chat-history"
    text="Your conversation history with the AI tutor. Click any past chat to continue the conversation or review what you learned."
    order={STEP_ORDER.CHAT_HISTORY}
    style={style}
  >
    {children}
  </WalkthroughStep>
);

export const ChaptersTabStep = ({ children, style }) => (
  <WalkthroughStep
    name="sidebar-chapters"
    text="Browse all subjects, chapters, and topics. Click any topic to load its content, videos, and 3D models."
    order={STEP_ORDER.SIDEBAR_CHAPTERS_TAB}
    style={style}
  >
    {children}
  </WalkthroughStep>
);

export const TopicTilesStep = ({ children, style }) => (
  <WalkthroughStep
    name="topic-tiles"
    text="Topic cards show available learning content. Click the play button to watch videos, or tap the card to explore 3D models and interactive content."
    order={STEP_ORDER.TOPIC_TILES}
    style={style}
  >
    {children}
  </WalkthroughStep>
);

export const ViewModeToggleStep = ({ children, style, active = true }) => (
  <WalkthroughStep
    name="view-mode-toggle"
    text="Switch between Curriculum and Collection modes. Curriculum follows your school's syllabus structure, while Collections are custom learning modules created by your teacher."
    order={STEP_ORDER.VIEW_MODE_TOGGLE}
    style={style}
    active={active}
  >
    {children}
  </WalkthroughStep>
);

export const ContentAreaStep = ({ children, style }) => (
  <WalkthroughStep
    name="content-area"
    text="Main learning area: View 3D models, watch videos, and explore interactive content for each topic."
    order={STEP_ORDER.CONTENT_AREA}
    style={style}
  >
    {children}
  </WalkthroughStep>
);

export const AIChatDockStep = ({ children, style }) => (
  <WalkthroughStep
    name="ai-chat-dock"
    text="Your AI tutor! Ask any question about the current topic or anything else. Use the microphone for voice input, or type your question."
    order={STEP_ORDER.AI_CHAT_DOCK}
    style={style}
  >
    {children}
  </WalkthroughStep>
);

export default {
  STEP_ORDER,
  WalkthroughStep,
  // Student steps
  ResumeCardStep,
  StudentRecentQuizzesStep,
  UpcomingTestsStep,
  StreakCardStep,
  RecentActivitiesStep,
  // Teacher steps
  TeacherStatsStep,
  TeacherQuickActionsStep,
  ActivityFeedStep,
  LeaderboardStep,
  EngagementChartStep,
  // Learning steps
  SidebarTabsStep,
  ChatHistoryStep,
  ChaptersTabStep,
  TopicTilesStep,
  ViewModeToggleStep,
  ContentAreaStep,
  AIChatDockStep,
};
